import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import {
	PlanConfig,
	SubscriptionActions,
	SubscriptionModalState,
	SubscriptionStatus,
	SubscriptionStatusResponse
} from '@models/subscription.model';
import { map } from 'rxjs/operators';
import { StringModifierServiceProvider } from '@services/helpers/string-modifier-service/stringModifier.service';
import { IframeService } from '@services/helpers/iframe-service/iframe.service';
import { GeneralAction } from '@models/models';
import { AuthTokensService } from '../../../web/modern/auth-tokens.service';

@Injectable()
export class NewSubscriptionServiceDataProvider {
	private subscriptionData$: BehaviorSubject<
		SubscriptionStatus
	> = new BehaviorSubject<SubscriptionStatus>(null);
	private choosePlanModalOpenStatus$: BehaviorSubject<
		SubscriptionModalState<PlanConfig>
	> = new BehaviorSubject<SubscriptionModalState<PlanConfig>>({
		state: false,
		payload: null
	});

	constructor(
		private http: HttpClient,
		private stringModifierService: StringModifierServiceProvider,
		private iframeService: IframeService,
		private tokenService: AuthTokensService
	) {}

	// Observables

	get subscriptionData(): Observable<SubscriptionStatus> {
		return this.subscriptionData$.asObservable();
	}

	get choosePlanModalOpenStatus(): Observable<
		SubscriptionModalState<PlanConfig>
	> {
		return this.choosePlanModalOpenStatus$.asObservable();
	}

	setSubscriptionData(data: SubscriptionStatus): void {
		this.subscriptionData$.next(data);
	}

	setChoosePlanOpenModalStatus(
		state: SubscriptionModalState<PlanConfig>
	): void {
		this.choosePlanModalOpenStatus$.next(state);
	}

	// API

	getSubscriptions(customUrl: string): Observable<Object> {
		const url = customUrl || '/api/basic/v1/subscription/plans';

		if (customUrl) {
			const token = this.tokenService.getAuthToken();

			const headers = new HttpHeaders({
				Authorization: `Bearer ${token}`
			});
			const options = { headers };

			return this.http.get(url, options);
		}

		return this.http.get(url);
	}

	getSubscriptionStatus(): Observable<Object> {
		const url = '/api/basic/v1/subscription/status';

		return this.http.get(url);
	}

	cancelSubscription(): Observable<Object> {
		const url = '/api/basic/v1/subscription/cancel';
		const body = {};

		return this.http.post(url, body);
	}

	sendFeedback(feedback: string): Observable<Object> {
		const url = '/protected/rest/unsubscribe/send';
		const headers = new HttpHeaders().set(
			'Content-Type',
			'application/x-www-form-urlencoded'
		);
		const body = this.stringModifierService.urlEncodDataTransform({ feedback });
		return this.http.post(url, body, { headers });
	}

	// LOGIC

	fetchSubscriptionStatus(): Observable<SubscriptionStatus> {
		return this.getSubscriptionStatus().pipe(
			map((data: SubscriptionStatusResponse): SubscriptionStatus => data.data)
		);
	}

	onSubscriptionExpired(): void {
		this.setChoosePlanOpenModalStatus({ state: true, payload: null });
	}

	createActionList(actions: SubscriptionActions): Array<string> {
		return Object.keys(actions);
	}

	performAfterLoginActions(actions: SubscriptionActions): void {
		if (actions.CHOOSE_PLAN) {
			this.setChoosePlanOpenModalStatus({ state: true, payload: null });
			return;
		}

		this.createActionList(actions).forEach((item: string): void => {
			const action: GeneralAction = actions[item];

			if (action.method === 'FRAME') {
				this.iframeService.setUrlIframe(action.url);
			}
		});
	}

	updateStatus(): Observable<void> {
		return this.fetchSubscriptionStatus().pipe(
			map((data: SubscriptionStatus): void =>
				this.successGetStatusForUpdate(data)
			)
		);
	}

	private successGetStatusForUpdate(data: SubscriptionStatus): void {
		this.setSubscriptionData(data);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./shared/services/subscription/subscription.service.ts